import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import type { CloneOrderProvider } from '@/infrastructure/FulfillmentAdminClient';

interface CreateCarrierOrderModalProps {
  readonly show: boolean;
  readonly orderId: number;
  /** The carrier a clone was made for, offered first; null when the order names none. */
  readonly madeFor: string | null;
  readonly onHide: () => void;
  /** The carrier picked, or undefined to let the server route by the destination country. */
  readonly onConfirm: (provider: CloneOrderProvider | undefined) => void;
}

/** The carriers a clone can be made for, so the clone's own booking can always offer the one it was made for. */
const PROVIDERS: Array<{ value: CloneOrderProvider; label: string }> = [
  { value: 'DELEO', label: 'DELEO' },
  { value: 'YUN_EXPRESS', label: 'YUN EXPRESS' },
  { value: 'PARXL', label: 'PARXL' },
];

const ROUTE_BY_COUNTRY = '';

const TITLE_ID = 'create-carrier-order-title';

const labelOf = (provider: string): string => PROVIDERS.find((choice) => choice.value === provider)?.label ?? provider;

const providerOf = (value: string | null): CloneOrderProvider | null =>
  PROVIDERS.find((choice) => choice.value === value?.toUpperCase())?.value ?? null;

/**
 * Books a 2.0 order's first carrier order with the carrier the operator picks (SSD-7399). Sending none routes by
 * the destination country, which for a clone can quietly pick a carrier other than the one it was made for — a
 * Saudi reship made for YunExpress went back to Deleo that way — so a clone opens on its own carrier.
 */
export function CreateCarrierOrderModal({ show, orderId, madeFor, onHide, onConfirm }: CreateCarrierOrderModalProps) {
  const [provider, setProvider] = useState<string>(ROUTE_BY_COUNTRY);
  const madeForProvider = providerOf(madeFor);

  // Each opening starts from the clone's own carrier, whatever was picked the last time.
  useEffect(() => {
    if (show) {
      setProvider(madeForProvider ?? ROUTE_BY_COUNTRY);
    }
  }, [show, madeForProvider]);

  const picked = providerOf(provider);
  const strays = madeForProvider !== null && picked !== madeForProvider;

  return (
    <Modal show={show} onHide={onHide} aria-labelledby={TITLE_ID}>
      <Modal.Header closeButton>
        <Modal.Title as="h5" id={TITLE_ID}>
          Create Carrier Order #{orderId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="create-carrier-provider">
          <Form.Label>Shipping Provider *</Form.Label>
          <Form.Select value={provider} onChange={(event) => setProvider(event.target.value)}>
            <option value={ROUTE_BY_COUNTRY}>Route by destination country</option>
            {PROVIDERS.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        {madeForProvider && (
          <div className={`small mt-2 ${strays ? 'text-danger' : 'text-muted'}`} data-testid="carrier-made-for">
            {strays
              ? `This clone was made for ${labelOf(madeForProvider)}; ${
                  picked ? `this books ${labelOf(picked)} instead.` : 'routing by country may pick another carrier.'
                }`
              : `This clone was made for ${labelOf(madeForProvider)}.`}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => onConfirm(picked ?? undefined)}>
          Book Carrier Order
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
